<template>
  <div class="task-timeline">
    <div v-if="loading" class="loading">
      <i class="el-icon-loading"></i>
      <span>加载中...</span>
    </div>
    <div v-else-if="!rootTasks.length" class="empty">
      <i class="el-icon-document"></i>
      <p>暂无任务</p>
    </div>
    <div v-else class="timeline-container">
      <div 
        v-for="rootTask in rootTasks" 
        :key="rootTask.task_id"
        class="timeline-item root-task"
      >
        <!-- 根任务 -->
        <div class="timeline-node root-node">
          <el-tag :type="getStatusType(rootTask.status)">
            {{ getStatusText(rootTask.status) }}
          </el-tag>
        </div>
        <div class="timeline-content">
          <div class="task-card root-task-card">
            <div class="task-header">
              <h3 class="task-title">{{ rootTask.task_name }}</h3>
              <div class="task-meta">
                <span class="task-time">{{ formatTime(rootTask.create_time) }}</span>
                <el-tag size="small">{{ getTaskTypeText(rootTask.task_type) }}</el-tag>
              </div>
            </div>
            <div v-if="rootTask.task_description" class="task-description">
              {{ rootTask.task_description }}
            </div>
            
            <!-- 主任务和子任务 -->
            <div v-if="rootTask.children && rootTask.children.length" class="child-tasks">
              <div 
                v-for="mainTask in rootTask.children" 
                :key="mainTask.task_id"
                class="timeline-item main-task"
              >
                <div class="timeline-node main-node"></div>
                <div class="timeline-content">
                  <div class="task-card main-task-card">
                    <div class="task-header">
                      <h4 class="task-title">{{ mainTask.task_name }}</h4>
                      <div class="task-meta">
                        <span class="task-time">{{ formatTime(mainTask.create_time) }}</span>
                        <el-tag size="small" :type="getStatusType(mainTask.status)">
                          {{ getStatusText(mainTask.status) }}
                        </el-tag>
                      </div>
                    </div>
                    
                    <!-- 子任务 -->
                    <div v-if="mainTask.children && mainTask.children.length" class="child-tasks">
                      <div 
                        v-for="subTask in mainTask.children" 
                        :key="subTask.task_id"
                        class="timeline-item sub-task"
                      >
                        <div class="timeline-node sub-node"></div>
                        <div class="timeline-content">
                          <div class="task-card sub-task-card">
                            <div class="task-header">
                              <h5 class="task-title">{{ subTask.task_name }}</h5>
                              <div class="task-meta">
                                <span class="task-time">{{ formatTime(subTask.create_time) }}</span>
                                <el-tag size="small" :type="getStatusType(subTask.status)">
                                  {{ getStatusText(subTask.status) }}
                                </el-tag>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProjectRootTasks, getTaskHierarchy } from '../api/task'

export default {
  name: 'TaskTimeline',
  components: {},
  props: {
    projectId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      rootTasks: []
    }
  },
  created() {
    this.loadTasks()
  },
  methods: {
    async loadTasks() {
      this.loading = true
      try {
        // 获取项目根任务
        const rootTasksResponse = await getProjectRootTasks(this.projectId)
        if (rootTasksResponse && rootTasksResponse.tasks) {
          const rootTaskList = rootTasksResponse.tasks
          
          // 为每个根任务获取子任务层级
          const tasksWithHierarchy = await Promise.all(
            rootTaskList.map(async (rootTask) => {
              try {
                const hierarchyResponse = await getTaskHierarchy(rootTask.task_id)
                if (hierarchyResponse && hierarchyResponse.children) {
                  return {
                    ...rootTask,
                    children: hierarchyResponse.children || []
                  }
                }
                // 兼容旧格式
                if (hierarchyResponse && hierarchyResponse.data && hierarchyResponse.data.children) {
                  return {
                    ...rootTask,
                    children: hierarchyResponse.data.children || []
                  }
                }
                return rootTask
              } catch (hierarchyError) {
                console.error('获取任务层级失败:', hierarchyError)
                return rootTask
              }
            })
          )
          
          this.rootTasks = tasksWithHierarchy
        }
      } catch (error) {
        console.error('获取任务时间轴失败:', error)
        this.$message.error('获取任务时间轴失败')
      } finally {
        this.loading = false
      }
    },
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    },
    getStatusType(status) {
      const map = {
        1: '',
        2: 'warning',
        3: 'info',
        4: 'success',
        5: 'danger'
      }
      return map[status] || ''
    },
    getStatusText(status) {
      const map = {
        1: '待执行',
        2: '等依赖',
        3: '执行中',
        4: '已完成',
        5: '已失败'
      }
      return map[status] || '未知'
    },
    getTaskTypeText(type) {
      const map = {
        'manual': '手动',
        'automatic': '自动',
        'approval': '审批'
      }
      return map[type] || type
    }
  }
}
</script>

<style lang="scss" scoped>
.task-timeline {
  padding: 20px 0;
}

.loading,
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: var(--text-muted);
  
  i {
    font-size: 48px;
    margin-bottom: 12px;
  }
}

.timeline-container {
  position: relative;
}

.timeline-item {
  display: flex;
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.timeline-node {
  position: relative;
  flex-shrink: 0;
  
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 100%;
    width: 2px;
    height: 100%;
    background-color: var(--border-light);
    transform: translateX(-50%);
  }
  
  &:last-child::before {
    display: none;
  }
}

.root-node {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background-color: var(--primary-light);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  box-shadow: var(--shadow-card);
  transition: all var(--transition-base);
  position: relative;
  
  .el-tag {
    font-size: 12px;
    padding: 4px 8px;
    border-radius: var(--radius-sm);
    font-weight: 500;
  }
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 100%;
    width: 2px;
    height: calc(100% + 20px);
    background-color: var(--border-color-light);
    transform: translateX(-50%);
  }
  
  &:last-child::after {
    display: none;
  }
  
  &:hover {
    box-shadow: var(--shadow-hover);
    background-color: var(--primary-color);
    
    .el-tag {
      color: var(--text-white);
    }
  }
}

.main-node {
  width: 12px;
  height: 12px;
  border-radius: var(--radius-full);
  background-color: var(--success-light);
  margin: 8px 19px 0 19px;
  box-shadow: var(--shadow-card);
  transition: all var(--transition-base);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 100%;
    width: 2px;
    height: calc(100% + 16px);
    background-color: var(--border-color-light);
    transform: translateX(-50%);
  }
  
  &:last-child::after {
    display: none;
  }
  
  &:hover {
    background-color: var(--success-color);
    box-shadow: var(--shadow-hover);
  }
}

.sub-node {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background-color: var(--info-light);
  margin: 10px 21px 0 21px;
  box-shadow: var(--shadow-card);
  transition: all var(--transition-base);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 100%;
    width: 2px;
    height: calc(100% + 16px);
    background-color: var(--border-color-light);
    transform: translateX(-50%);
  }
  
  &:last-child::after {
    display: none;
  }
  
  &:hover {
    background-color: var(--info-color);
    box-shadow: var(--shadow-hover);
  }
}

.timeline-content {
  flex: 1;
  min-width: 0;
}

.task-card {
  background-color: var(--bg-card);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  transition: box-shadow var(--transition-base);
  padding: var(--spacing-lg);
  border: 1px solid var(--border-color-lighter);
  cursor: pointer;
  
  &:hover {
    box-shadow: var(--shadow-hover);
    transform: translateY(-2px);
    border-color: var(--primary-light);
  }
}

.root-task-card {
  border-left: 4px solid var(--primary-color);
}

.main-task-card {
  border-left: 4px solid var(--success-color);
  margin-left: 20px;
}

.sub-task-card {
  border-left: 4px solid var(--info-color);
  margin-left: 40px;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.task-title {
  margin: 0;
  font-weight: 600;
  
  &.root-task {
    font-size: 18px;
    color: var(--text-primary);
  }
  
  &.main-task {
    font-size: 16px;
    color: var(--text-primary);
  }
  
  &.sub-task {
    font-size: 14px;
    color: var(--text-regular);
  }
}

.task-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-muted);
}

.task-time {
  white-space: nowrap;
}

.task-description {
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
}

.child-tasks {
  margin-top: 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .task-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .main-task-card {
    margin-left: 10px;
  }
  
  .sub-task-card {
    margin-left: 20px;
  }
}
</style>